import { useCallback, useState } from "react";
import type { MessageItem, TimelineItem } from "../ConversationTimeline";

export type OptimisticMessage = MessageItem & {
  message: MessageItem["message"] & { isOptimistic: true };
};

// Owns the optimistic outbound-message buffer shared by a timeline and its
// composer: the composer adds/removes/marks-failed, the timeline renders the
// merge. Used by LeadConversationPanel, the one conversation view both the
// inbox and the tablero mount.
export const useOptimisticMessages = () => {
  const [optimisticMessages, setOptimisticMessages] = useState<OptimisticMessage[]>([]);

  const add = useCallback((message: OptimisticMessage) => {
    setOptimisticMessages((prev) => [...prev, message]);
  }, []);

  const remove = useCallback((id: string) => {
    setOptimisticMessages((prev) => prev.filter((msg) => msg.message.id !== id));
  }, []);

  const markFailed = useCallback((id: string) => {
    setOptimisticMessages((prev) =>
      prev.map((msg) =>
        msg.message.id === id
          ? { ...msg, message: { ...msg.message, lastStatus: "failed" } }
          : msg
      )
    );
  }, []);

  const mergeWithTimeline = useCallback(
    (baseItems: TimelineItem[]): TimelineItem[] => {
      // Filter out optimistic messages that have a matching real message
      // This prevents flicker when refetch completes but before state cleanup
      const pendingOptimistic = optimisticMessages.filter((opt) => {
        const hasMatchingRealMessage = baseItems.some((item) => {
          if (item.type !== "message") return false;
          const realMsg = item.message;
          const optMsg = opt.message;

          // Must be same source
          if (realMsg.source !== optMsg.source) return false;

          // Check timestamps are within 30 seconds
          const realTime = new Date(realMsg.createdAt).getTime();
          const optTime = new Date(optMsg.createdAt).getTime();
          if (Math.abs(realTime - optTime) > 30000) return false;

          // For text messages, match by content
          if (optMsg.content && optMsg.content === realMsg.content) return true;

          // For media messages (empty content), match by media type
          if (!optMsg.content && !realMsg.content) {
            if (optMsg.image && realMsg.image) return true;
            if (optMsg.video && realMsg.video) return true;
            const optDoc = (optMsg as { document?: { url: string } }).document;
            const realDoc = (realMsg as { document?: { url: string } }).document;
            if (optDoc && realDoc) return true;
            if (optMsg.audio && realMsg.audio) return true;
          }

          return false;
        });

        return !hasMatchingRealMessage;
      });

      // Combine and sort by timestamp
      const combined = [...baseItems, ...pendingOptimistic];
      return combined.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
    },
    [optimisticMessages]
  );

  return { optimisticMessages, add, remove, markFailed, mergeWithTimeline };
};
